/**
 * Built-in workflows rendered in the SAME visual language as composed ones —
 * verb chip, sentence row, numbered node, outcome-labeled arm wells. Their
 * steps are catalog sentences (strings) rather than editable definitions, so
 * this is a presentational twin of StepList, not a variant of it. The one
 * editable slot (who approves) expands inline under its step, exactly like a
 * composed step's config drawer.
 */
import type { WorkflowBranch, WorkflowStep } from '../../lib/types.js';

/** Catalog step ids are already verbs ('diagnose', 'await-approval'). */
const verbOf = (id: string): string => id.replace(/[-_]/g, ' ').split(' ')[0] ?? id;

const Node = ({ label, diamond }: { label: string; diamond?: boolean }) =>
  diamond ? (
    <span className="flex h-6 w-6 flex-none rotate-45 items-center justify-center rounded border border-warning bg-surface-elevated text-xs text-warning">
      <span className="-rotate-45">✋</span>
    </span>
  ) : (
    <span className="flex h-6 w-6 flex-none items-center justify-center rounded-full border border-outline-strong bg-surface-elevated text-[0.68rem] font-semibold text-ink-muted">
      {label}
    </span>
  );

const Row = ({
  index,
  step,
  last,
  children,
}: {
  index: number;
  step: WorkflowStep;
  last: boolean;
  children?: React.ReactNode;
}) => (
  <div className="flex min-w-0 gap-3">
    <div className="flex flex-col items-center pt-1.5">
      <Node label={String(index + 1)} diamond={step.configurable === 'approvers'} />
      {!last && <span className="w-px flex-1 bg-outline" />}
    </div>
    <div className="min-w-0 flex-1">
      <div
        className={`flex min-w-0 items-baseline gap-1.5 border border-outline bg-surface px-3 py-2 text-sm ${
          children ? 'rounded-t-lg border-b-0' : 'rounded-lg'
        }`}
      >
        {/* Grows past the alignment width rather than clipping — catalog verbs
            run from "load" to "investigate". */}
        <span className="min-w-[4rem] flex-none pr-2 text-[0.68rem] font-semibold uppercase tracking-wider text-ink-subtle">
          {verbOf(step.id)}
        </span>
        <span className="min-w-0">
          {step.sentence}
          {step.sub && <span className="block text-xs text-ink-subtle">{step.sub}</span>}
        </span>
      </div>
      {children}
    </div>
  </div>
);

export const ReadOnlySteps = ({
  steps,
  branches,
  slot,
}: {
  steps: WorkflowStep[];
  branches?: WorkflowBranch[];
  /** Rendered inline under the step that declares a configurable slot. */
  slot?: React.ReactNode;
}) => (
  <div className="grid min-w-0 gap-2">
    {steps.map((s, i) => (
      <Row key={s.id} index={i} step={s} last={i === steps.length - 1 && !branches?.length}>
        {s.configurable === 'approvers' && slot ? (
          <div className="rounded-b-lg border border-t-0 border-outline-strong bg-surface-elevated p-3">
            {slot}
          </div>
        ) : null}
      </Row>
    ))}
    {(branches ?? []).map((b) => (
      <div
        key={b.label}
        className="ml-2 rounded-lg border border-outline bg-surface-elevated/70 p-3"
      >
        <span className="text-[0.72rem] font-semibold uppercase tracking-wider text-ink-subtle">
          {b.label}
        </span>
        <div className="mt-2 grid gap-2">
          {b.steps.map((s, i) => (
            <Row key={s.id} index={i} step={s} last={i === b.steps.length - 1} />
          ))}
        </div>
      </div>
    ))}
  </div>
);
